import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

/**
 * A custom element that manages the FBT Bundle functionality.
 * Collects form data from checked variant selections.
 *
 * @typedef {object} FbtBundleRefs
 * @property {HTMLButtonElement} openButton - The button to open the dialog.
 * @property {HTMLButtonElement} closeButton - The button to close the dialog.
 *
 * @extends Component<FbtBundleRefs>
 */
export class FbtBundleComponent extends Component {
  /** @type {string[]} */
  requiredRefs = [];

  /**
   * Gets the dialog element from the nested dialog-component.
   *
   * @returns {HTMLDialogElement | null} The dialog element or null if not found.
   */
  get dialog() {
    const dialogComponent = this.querySelector('dialog-component');
    return /** @type {HTMLDialogElement | null} */ (dialogComponent?.querySelector('dialog'));
  }

  /**
   * Gets the open button from the nested dialog-component.
   *
   * @returns {HTMLButtonElement | null} The open button or null if not found.
   */
  get openButton() {
    const dialogComponent = this.querySelector('dialog-component');
    return /** @type {HTMLButtonElement | null} */ (dialogComponent?.querySelector('[ref="openButton"]'));
  }

  /**
   * Gets the close button from the nested dialog-component.
   *
   * @returns {HTMLButtonElement | null} The close button or null if not found.
   */
  get closeButton() {
    const dialogComponent = this.querySelector('dialog-component');
    return /** @type {HTMLButtonElement | null} */ (dialogComponent?.querySelector('[ref="closeButton"]'));
  }

  /**
   * Gets all variant forms within the dialog.
   *
   * @returns {HTMLFormElement[]} Array of variant picker forms.
   */
  getVariantForms() {
    const dialog = this.dialog;
    if (!dialog) return [];
    return Array.from(dialog.querySelectorAll('.fbtb-dialog-item-variant-picker__form'));
  }

  /**
   * Collects all checked variant values from the forms.
   *
   * @returns {Array<{productId: string, variantId: string, variantTitle: string, productTitle: string}>} Array of selected variants.
   */
  getCheckedVariants() {
    const forms = this.getVariantForms();
    const checkedVariants = /** @type {Array<{productId: string, variantId: string, variantTitle: string, productTitle: string}>} */ ([]);

    forms.forEach((form) => {
      const checkedInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="radio"]:checked'));
      if (checkedInput) {
        const productTitle = form.closest('.fbtb-dialog-item')?.querySelector('.fbtb-dialog-item-title')?.textContent?.trim() ?? '';
        const productId = checkedInput.name?.match(/Size-\d+-(\d+)/)?.[1] ?? '';

        checkedVariants.push({
          productId,
          variantId: checkedInput.value,
          variantTitle: checkedInput.getAttribute('aria-label') ?? '',
          productTitle,
        });
      }
    });

    return checkedVariants;
  }

  /**
   * Gets the total price of all checked variants.
   *
   * @returns {number} Total price in dollars.
   */
  getTotalPrice() {
    const variants = this.getCheckedVariants();
    let total = 0;

    variants.forEach((variant) => {
      const priceElement = this.dialog?.querySelector(
        `[data-variant-id="${variant.variantId}"] .fbtb-dialog-item-price`
      );
      if (priceElement) {
        const priceText = priceElement.textContent?.replace(/[^\d.]/g, '');
        total += parseFloat(priceText) || 0;
      }
    });

    return total;
  }

  /**
   * Updates the total price display in the dialog.
   */
  updateTotalDisplay() {
    const total = this.getTotalPrice();
    const totalElement = this.dialog?.querySelector('.fbtb-total-amount');
    if (totalElement) {
      totalElement.textContent = `$${total.toFixed(2)}`;
    }
  }

  /**
   * Handles the add to bundle action.
   * Collects all checked variants and prepares for cart addition.
   */
  async handleAddToBundle() {
    console.dir("Hi");
    const checkedVariants = this.getCheckedVariants();

    if (checkedVariants.length === 0) {
      console.warn('No variants selected for bundle');
      return;
    }

    // Update total display
    // this.updateTotalDisplay();

    // Dispatch custom event with selected variants
    this.dispatchEvent(new CustomEvent('fbt-bundle:items-selected', {
      detail: {
        items: checkedVariants.map(v => ({
          id: parseInt(v.variantId),
          quantity: 1,
        })),
        total: this.getTotalPrice(),
      },
    }));
    const items = checkedVariants.map(v => ({
      id: parseInt(v.variantId),
      quantity: 1,
    }));

    await this.#addToCart(items);


    console.dir(checkedVariants);
    console.log('Selected bundle items:', checkedVariants);
  }
  /**
    * Adds items to the cart and triggers cart update event.
    *
    * @param {Array<{id: number, quantity: number}>} items - Items to add to cart.
    */
  async #addToCart(items) {

    this.loader?.classList.add("active");

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.id,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.id),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {

          return;
        }



        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'fbt-bundle-component',
            sourceId: "fbt-bundle-1234",
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.loader?.classList.remove("active");
        this.closeDialog()
      })
      ;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector("cart-items-component");

    if (cartItemsComponent) {
      await customElements.whenDefined("cart-items-component");
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: {
        Accept: "application/json",
      },
      credentials: "same-origin",
    }).then((response) => {
      if (!response.ok)
        throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }
  /**
   * Shows the dialog.
   */
  showDialog() {
    const dialog = this.dialog;
    if (dialog) {
      dialog.showModal();
      // Update total display when dialog opens
      // this.updateTotalDisplay();
    }
  }

  /**
   * Closes the dialog.
   */
  closeDialog() {
    const dialog = this.dialog;
    if (dialog) {
      dialog.close();
    }
  }

  /**
   * Handles form change events to update total.
   *
   * @param {Event} event - The change event.
   */
  handleFormChange(event) {
    // Update total when a variant is selected
    // this.updateTotalDisplay();
  }

  connectedCallback() {
    super.connectedCallback();
    console.dir("connectedCallback");
    // Add change listeners to all variant forms
    this.loader = this.querySelector(".loading-spinner-wrapper");

    const forms = this.getVariantForms();
    forms.forEach((form) => {
      form.addEventListener('change', this.handleFormChange.bind(this));
    });
  }

  disconnectedCallback() {
    // Remove change listeners
    const forms = this.getVariantForms();
    forms.forEach((form) => {
      form.removeEventListener('change', this.handleFormChange);
    });

    super.disconnectedCallback();
  }
}

if (!customElements.get('fbt-bundle-component')) {
  customElements.define('fbt-bundle-component', FbtBundleComponent);
}